import React, { useMemo } from "react";
import { useFixedQuery } from "../util/api";
import { useParams } from "react-router-dom";
import { PERSON_QUERY } from "./personDetailQuery";
import Table from "../util/Table";
import { formatDate } from "../util/dates";
import { ClassresultLink, EventLink, ResultLink } from "../links/links";
import { Tooltip } from "../util/Tooltip";
import HighchartsReact from "highcharts-react-official";
import Highcharts from 'highcharts'
import { intRange } from "../util/numbers";
import InfiniteScroll from "react-infinite-scroller";

export const PersonDetail = (props) => {
  let { personId } = useParams();
  const pageSize = 100;
  const { loading, error, data, fetchMore } = useFixedQuery(PERSON_QUERY, {
    variables: {
      id: personId,
      first: pageSize,
      offset: 0,
    },
  });

  const events = useMemo(() => {
    const isLoadingOrNoData = loading || !data;
    return isLoadingOrNoData ? [] : data.person.resultSet.edges.map((el) => el.node);
  }, [data]);

  const fetchNextData = () => {
    if (!loading) {
      fetchMore({
        variables: {
          offset: events.length,
        },
      });
    }
  };
  const hasMoreData = !data ? true : data.person.resultSet.pageInfo.hasNextPage;


  const formatPercent = (value) =>
    value != null ? (value * 100).toFixed(0) + "%" : "";

  const formatTime = (seconds) =>
    Math.floor(seconds / 3600) +
    ":" +
    String(Math.floor(seconds / 60) % 60).padStart(2, "0") +
    ":" +
    String(seconds % 60).padStart(2, "0");

  const columns = useMemo(
    () => [
      {
        Header: "Dato",
        accessor: "classresult.event.date",
        Cell: ({ value }) => formatDate(value),
      },
      {
        Header: () => (
          <Tooltip tooltipText="Klikk på løpsnavn for å vise all info om løpet">
            Navn
          </Tooltip>
        ),
        accessor: "classresult.event.name",
        Cell: ({ row }) => {
          const result = row.original;
          return (
            <EventLink
              event={result.classresult.event}
              text={result.classresult.event.name}
            ></EventLink>
          );
        },
      },
      {
        Header: () => (
          <Tooltip tooltipText="Klikk på klassenavn for å vise alle løpere i klassen">
            Klasse
          </Tooltip>
        ),
        accessor: "classresult.name",
        Cell: ({ row }) => (
          <ClassresultLink classresult={row.original.classresult} />
        ),
      },
      {
        Header: (
          <Tooltip tooltipText="Klikk på tid for å vise strekktider">
            Tid
          </Tooltip>
        ),
        accessor: "time",
        Cell: ({ row }) => (
          <ResultLink
            result={row.original}
            text={formatTime(row.original.time)}
          />
        ),
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => value,
      },
      {
        Header: "% bak tet",
        accessor: "percentBehind",
        Cell: ({ value }) => formatPercent(value / 100),
      },
      {
        Header: "Min/km",
        accessor: "minutesPerKilometer",
        Cell: ({ value }) => value?.toFixed(1) ?? null,
      },
      {
        Header: (
          <Tooltip tooltipText="Anslått løpskapasitet (LK). Ved optimal orientering vil løperen ha en snittfart på LK ganger vinnerfart.">
            LK
          </Tooltip>
        ),
        accessor: "runcap",
        Cell: ({ value }) => formatPercent(value),
      },
      {
        Header: (
          <Tooltip tooltipText="Anslått orienteringskapasitet (OK). Et mål på hvor godt man orienterer sammenlignet med andre løpere i samme klasse. 50% er middels godt.">
            OK
          </Tooltip>
        ),
        accessor: "ocap",
        Cell: ({ value }) => formatPercent(value),
      },
    ],
    []
  );
  if (error) {
    console.error(
      `An error occurred loading person id ${personId}: Error:`,
      error
    );
    return <p>Det oppsto en feil. Vennligst prøv igjen.</p>;
  }
  if (!data) return <p>Laster...</p>;
  const { person } = data;

  const title = person.name;
  const plotResults = events
    .filter((r) => r.runcap > 0 && r.ocap > 0)
    .reverse();
  const lk = plotResults.map((r, i) => ({
    x: i,
    y: r.runcap * 100,
    name:
      formatDate(r.classresult.event.date) + ": " + r.classresult.event.name,
  }));

  const ok = plotResults.map((r, i) => ({
    x: i,
    y: r.ocap * 100,
    name:
      formatDate(r.classresult.event.date) + ": " + r.classresult.event.name,
  }));

  const average = (values) => values.reduce((a, b) => a+b, 0)/values.length;
  const moving_average = (values, i) => average(values.slice(Math.max(0, i-1), Math.min(values.length, i+2)))

  const lk_values = plotResults.map((r, i) => r.runcap*100);
  const smooth_lk = plotResults.map((r, i) => ({
    x: i,
    y: moving_average(lk_values, i)
  }));
  const ok_values = plotResults.map((r, i) => r.ocap*100);
  const smooth_ok = plotResults.map((r, i) => ({
    x: i,
    y: moving_average(ok_values, i)
  }));

  const numberOfTicks = 10;
  const tickStep = Math.max(Math.ceil(plotResults.length/numberOfTicks), 1);
  const tickPositions = intRange(plotResults.length).filter(i => i % tickStep == 0);

  const options = {
    chart: { type: "line", alignTicks: false, zoomType: "xy", height: 700 },
    credits: { enabled: false },
    annotations: [],
    annotationsOptions: { enabledButtons: false },
    tooltip: { shared: true },
    title: { text: "LK/OK" },
    legend: { title: { text: "Klikk for å velge løpere:" } },
    xAxis: [
      {
        endOnTick: false,
        startOnTick: false,
        tickPositions: tickPositions,
        labels: {
          formatter: function () {
            return formatDate(plotResults[this.value].classresult.event.date);
          },
        },
        title: { text: "Dato" },
      },
    ],
    yAxis: [
      {
        endOnTick: false,
        startOnTick: false,
        title: { text: "LK/OK (%)" },
        gridLineWidth: 0,
        minorGridLineWidth: 0,
        min: 0,
        max: 101,
      },
    ],
    series: [
      {
        marker: {
          enabled: true,
          symbol: "circle",
          fillColor: "rgba(0,0,0,0)",
          lineColor: "rgb(255,0,0)",
          lineWidth: 1,
        },
        states: { hover: { enabled: false } },
        lineWidth: 0,
        id: "q473kLbfJo",
        name: "LK",
        data: lk,
        color: "rgb(255,0,0)",
        tooltip: { valueDecimals: 0 },
        linkedTo: "BaFeAJMsBm",
      },
      {
        marker: {
          enabled: true,
          symbol: "square",
          fillColor: "rgba(0,0,0,0)",
          lineColor: "rgb(255,0,0)",
          lineWidth: 1,
        },
        states: { hover: { enabled: false } },
        lineWidth: 0,
        id: "Ah8zZtXlY2",
        name: "OK",
        data: ok,
        color: "rgb(255,0,0)",
        tooltip: { valueDecimals: 0 },
        linkedTo: "SJuJmPJMzm",
      },
      {
        marker: { enabled: false },
        states: { hover: { enabled: false } },
        lineWidth: 2,
        id: "QlWAsFbfJz",
        name: "LK (glatt)",
        data: smooth_lk,
        color: "rgb(255,0,0)",
        type: "spline",
        enableMouseTracking: false,
        linkedTo: "BaFeAJMsBm",
      },
      {
        marker: { enabled: false },
        states: { hover: { enabled: false } },
        lineWidth: 2,
        id: "kUdKHEoeIk",
        name: "OK (glatt)",
        data: smooth_ok,
        dashStyle: "Dash",
        color: "rgb(255,0,0)",
        type: "spline",
        enableMouseTracking: false,
        linkedTo: "SJuJmPJMzm",
      },
      {
        marker: {
          enabled: true,
          symbol: "circle",
          fillColor: "rgba(0,0,0,0)",
          lineColor: "rgb(255,0,0)",
          lineWidth: 1,
        },
        states: { hover: { enabled: false } },
        lineWidth: 2,
        id: "BaFeAJMsBm",
        name: "LK",
        data: [],
        color: "rgb(255,0,0)",
      },
      {
        marker: { enabled: true, symbol: "square", fillColor: "rgba(0,0,0,0)" },
        states: { hover: { enabled: false } },
        lineWidth: 2,
        id: "SJuJmPJMzm",
        name: "OK",
        data: [],
        dashStyle: "Dash",
        color: "rgb(255,0,0)",
      },
    ],
  };
  return (
    <>
      <h2>{title}</h2>
      Har deltatt i {person.resultSet.count} løp:
      <InfiniteScroll
        loadMore={fetchNextData}
        hasMore={hasMoreData}
        loader={<p key={0}>Laster...</p>}
      >
        <Table
          columns={columns}
          data={events}
          disableSortBy={false}
          showFooter={false}
        />
      </InfiniteScroll>
      <HighchartsReact highcharts={Highcharts} options={options} />
    </>
  );
};
